import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { IconButton } from '@/components/common/IconButton';
import { Plus, Search, X, Filter, Eye, FileStack, DollarSign, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { isFundingRoutingOn } from '@/lib/fundingRoutingFlag';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, labelFor, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';

// Status filter pills — keep the set small and aligned with the canonical
// claim lifecycle states. 'all' is the implicit default.
const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'READY', label: 'Ready' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'PAID', label: 'Paid' },
  { key: 'PART_PAID', label: 'Part-paid' },
  { key: 'REJECTED', label: 'Rejected' },
];

export default function ClaimListPage() {
  const [claims, setClaims] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [participants, setParticipants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [genDialog, setGenDialog] = useState(false);
  const [genForm, setGenForm] = useState({ branch_id: '', date_from: '', date_to: '', participant_id: '', grouping_mode: 'BY_DATE_RANGE', payer_type: '' });
  const routingOn = isFundingRoutingOn();
  const [genLoading, setGenLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20, sortBy: 'created_at', sortDir: 'desc' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const [clmRes, pRes, bRes] = await Promise.all([
        api.get(`/claims?${params}`), api.get('/participants?pageSize=100'), api.get('/org/branches'),
      ]);
      setClaims(clmRes.data.items || clmRes.data.data || []);
      setTotal(clmRes.data.total || 0);
      setParticipants(pRes.data.items || pRes.data.data || []);
      setBranches(bRes.data.data || []);
    } catch (e) { notify.error(e, 'CLAIM_LIST_LOAD_FAILED'); }
    finally { setListLoading(false); }
  };
  useEffect(() => { fetchData(); }, [page, statusFilter]); // eslint-disable-line

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const body = { date_from: genForm.date_from, date_to: genForm.date_to, grouping_mode: genForm.grouping_mode };
      if (genForm.branch_id) body.branch_id = genForm.branch_id;
      if (genForm.participant_id) body.participant_id = genForm.participant_id;
      if (!routingOn && genForm.payer_type) body.payer_type = genForm.payer_type;
      const res = await api.post('/claims/generate', body);
      const cd = res.data.data || {};
      const blocked = cd.per_participant_billing_blocked || [];
      if (cd.per_participant_billing_status === 'COMPLETED_WITH_BLOCKS' || blocked.length > 0) {
        toast.warning(`Claim ${cd.claim_no} generated with ${blocked.length} blocked participant line(s)`, {
          description: 'Some residents were not billed. Review the billing blocks on the claim.',
        });
      } else {
        toast.success(`Claim ${cd.claim_no} generated`);
      }
      const routing = (res.data.validation_errors || []).filter(v => ['WRONG_FUNDING_CHANNEL', 'FUNDING_METHOD_NOT_SET'].includes(v.error));
      if (routing.length > 0) {
        toast.warning(`${routing.length} timesheet(s) excluded by funding routing`, { description: routing[0].message, duration: 8000 });
      }
      setGenDialog(false); fetchData();
      navigate(`/finance/claims/${res.data.data.id}`);
    } catch (e) { notify.error(e, 'CLAIM_GENERATE_FAILED'); }
    finally { setGenLoading(false); }
  };

  // Client-side text filter is additive to the server status filter — the
  // server is the source of truth for paging/total.
  const visibleClaims = claims.filter(c =>
    !search ||
    c.claim_no?.toLowerCase().includes(search.toLowerCase()) ||
    c.branch_id?.toLowerCase().includes(search.toLowerCase())
  );

  // Summary KPIs (read-only aggregation; no calculation logic change).
  const summary = useMemo(() => {
    const sum = (list) => list.reduce((s, c) => s + (c.totals?.amount || 0), 0);
    return {
      count: total,
      totalAmt: sum(claims),
      submitted: claims.filter(c => c.status === 'SUBMITTED').length,
      paid: sum(claims.filter(c => c.status === 'PAID' || c.status === 'PART_PAID')),
      rejected: claims.filter(c => c.status === 'REJECTED').length,
    };
  }, [claims, total]);

  const hasActiveFilters = search || statusFilter !== 'all';
  const isEmptySource = !listLoading && claims.length === 0 && !hasActiveFilters;
  const isFilteredEmpty = !listLoading && claims.length > 0 && visibleClaims.length === 0;
  const isStatusFilteredEmpty = !listLoading && claims.length === 0 && hasActiveFilters;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="claims-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Claim Batches</h1>
          <p className="text-sm text-muted-foreground mt-1">Generate, group and track NDIS claim batches.</p>
        </div>
        <Dialog open={genDialog} onOpenChange={setGenDialog}>
          <DialogTrigger asChild><Button data-testid="generate-claim"><Plus className="h-4 w-4 mr-2" /> Generate Claim</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Generate Claim Batch</DialogTitle><DialogDescription>Select approved timesheets to include</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Date From</Label><Input type="date" value={genForm.date_from} onChange={(e) => setGenForm({...genForm, date_from: e.target.value})} required /></div>
                <div className="space-y-2"><Label>Date To</Label><Input type="date" value={genForm.date_to} onChange={(e) => setGenForm({...genForm, date_to: e.target.value})} required /></div>
              </div>
              <div className="space-y-2"><Label>Branch (optional)</Label>
                <Select value={genForm.branch_id || '__all'} onValueChange={(v) => setGenForm({...genForm, branch_id: v === '__all' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent><SelectItem value="__all">All branches</SelectItem>{branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Participant (optional)</Label>
                <AsyncSearchSelect
                  endpoint="/participants"
                  value={genForm.participant_id}
                  onChange={(v) => setGenForm({...genForm, participant_id: v || ''})}
                  getOptionLabel={participantLabel}
                  selectedLabel={labelFor(participants, genForm.participant_id, participantLabel)}
                  placeholder="All participants"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  clearLabel="All participants"
                  testId="claim-gen-participant"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Grouping</Label>
                  <Select value={genForm.grouping_mode} onValueChange={(v) => setGenForm({...genForm, grouping_mode: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BY_DATE_RANGE">By Date Range</SelectItem>
                      <SelectItem value="BY_PARTICIPANT">By Participant</SelectItem>
                      <SelectItem value="BY_BRANCH">By Branch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {routingOn ? (
                  <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900" data-testid="claim-funding-routing-note">
                    <span className="font-semibold">Payer: NDIA (agency-managed)</span>
                    <p>Only timesheets whose Service Agreement is <b>agency-managed</b> enter this batch. Self- and plan-managed supports are invoiced instead; agreements with no funding method set are held back.</p>
                  </div>
                ) : (
                  <div className="space-y-2"><Label>Payer Type (optional)</Label>
                    <Select value={genForm.payer_type || '__none'} onValueChange={(v) => setGenForm({...genForm, payer_type: v === '__none' ? '' : v})}>
                      <SelectTrigger data-testid="claim-payer-type-select"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Any</SelectItem>
                        <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
                        <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
                        <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
                        <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button className="w-full" onClick={handleGenerate} disabled={genLoading || !genForm.date_from || !genForm.date_to} data-testid="generate-claim-submit">
                {genLoading ? 'Generating...' : 'Generate Claim Batch'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="claims-summary">
        <Card className="border-slate-200">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><FileStack className="h-3.5 w-3.5" /> Total batches</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="claims-summary-count">{summary.count}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><DollarSign className="h-3.5 w-3.5" /> Total value</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="claims-summary-total">${summary.totalAmt.toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card className="border-sky-200 bg-sky-50/40">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-sky-700"><AlertCircle className="h-3.5 w-3.5" /> Submitted (page)</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-sky-900" data-testid="claims-summary-submitted">{summary.submitted}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Paid (page)</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-emerald-900" data-testid="claims-summary-paid">${summary.paid.toFixed(2)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:w-[340px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by claim # or branch..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="claim-search" />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="claim-status-filters">
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {STATUS_FILTERS.map(s => (
            <button
              key={s.key}
              onClick={() => { setStatusFilter(s.key); setPage(1); }}
              data-testid={`claim-status-filter-${s.key.toLowerCase()}`}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${statusFilter === s.key ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {s.label}
            </button>
          ))}
          {hasActiveFilters && (
            <button
              onClick={() => { setSearch(''); setStatusFilter('all'); setPage(1); }}
              data-testid="claim-filters-clear"
              className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          )}
          <span className="shrink-0 text-xs text-muted-foreground ml-1">{visibleClaims.length} shown</span>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          {listLoading ? (
            <TableSkeleton rows={6} columns={7} showHeader headers={['Claim No', 'Period', 'Branch', 'Items', 'Amount', 'Status', '']} testid="claims-loading" />
          ) : isEmptySource ? (
            <EmptyState
              icon={FileStack}
              title="No claim batches yet"
              description="Generate your first claim batch from approved timesheets to start submitting NDIS payments."
              testid="claims-empty"
              action={
                <Button onClick={() => setGenDialog(true)} data-testid="claims-empty-cta">
                  <Plus className="h-4 w-4 mr-2" /> Generate Claim Batch
                </Button>
              }
            />
          ) : (isFilteredEmpty || isStatusFilteredEmpty) ? (
            <EmptyState
              icon={Search}
              title="No matches"
              description="No claim batches match the current search or status filter."
              testid="claims-empty-filtered"
              compact
              action={
                <Button variant="outline" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setPage(1); }} data-testid="claims-empty-filtered-cta">
                  Clear filters
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Claim No</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleClaims.map(c => (
                    <TableRow key={c.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/finance/claims/${c.id}`)} data-testid={`claim-row-${c.id}`}>
                      <TableCell className="font-mono text-xs">{c.claim_no}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{c.date_from} to {c.date_to}</TableCell>
                      <TableCell>{c.branch_id || 'All'}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.totals?.count || c.line_items?.length || 0}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-medium">${(c.totals?.amount || 0).toFixed(2)}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          label="View claim batch"
                          testid={`claim-view-${c.id}`}
                          onClick={() => navigate(`/finance/claims/${c.id}`)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <PaginationFooter page={page} pageSize={20} total={total} onPageChange={setPage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
